import React, { useEffect, useState } from "react";

import { Button } from "primereact/button";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Sidebar } from "primereact/sidebar";
import { CellType } from "utils/enums";
import { T } from "services/lang/LanguageService";
import { isObjectEmpty } from "utils/utils";
import { Filter, ValidationType, ValidationTypeInfo, commonValidationTypes, validationTypes } from "../utils/interfaces";
import { extractFilters } from "../utils/utils";
import { ProductTemplateFilterProps } from "./ProductTemplateFilterTypes";

const unsupportedValidations: ValidationType[] = ["NOT_EMPTY", "REGEX", "ENDS_WITH", "MAX_LENGTH", "MIN_LENGTH"];

const stringValidations: ValidationType[] = ["STARTS_WITH", "CONTAINS"];

const ProductTemplateFilter: React.FC<ProductTemplateFilterProps> = ({
  defaultFilter,
  secondaryFilter,
  attributes,
  onApplyFilter,
}) => {
  const [filterVisible, setFilterVisible] = useState<boolean>(false);
  const [filters, setFilters] = useState<Filter[]>(() =>
    defaultFilter ? extractFilters(defaultFilter) : [{ reference: "", validation: "", value: "" }]
  );
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (secondaryFilter !== null) {
      setFilters(secondaryFilter ? extractFilters(secondaryFilter) : [{ reference: "", validation: "", value: "" }]);
    }
  }, [secondaryFilter]);

  const handleChange = (e: DropdownChangeEvent | React.ChangeEvent<HTMLInputElement>, i: number) => {
    if (error) setError("");
    const { name, value } = e.target;
    setFilters(prev => prev.map((p, j) => (j === i ? { ...p, [name]: value } : p)));
  };

  const onApply = () => {
    onApplyFilter(filters.map(f => `${f.reference}:${f.validation}:"${f.value.replaceAll('"', '\\"')}"`).join(";"));
    setFilterVisible(false);
  };

  const addNewFilter = () => {
    const emptyFieldFilter = filters.find(f => !f?.reference || !f?.validation || f?.value === "");
    if (!emptyFieldFilter || isObjectEmpty(filters)) {
      setFilters(prev => [...prev, { reference: "", validation: "", value: "" }]);
      return;
    }
    setError(T("error.all-filter-fields-are-required"));
  };

  const removeFilter = (i: number) => {
    if (error) setError("");
    setFilters(prev => prev.toSpliced(i, 1));
  };

  const setValidationOptions = (cellType: CellType | undefined) => {
    if (!cellType) return [];
    let validationOptions: Readonly<ValidationTypeInfo[]> = [];
    switch (cellType) {
      case CellType.LONG:
      case CellType.DOUBLE:
        validationOptions = validationTypes.filter(
          v => ![...unsupportedValidations, ...stringValidations].includes(v.short as ValidationType)
        );
        break;
      default:
        validationOptions = commonValidationTypes.filter(v => !unsupportedValidations.includes(v.short as ValidationType));
        break;
    }
    return validationOptions as ValidationTypeInfo[];
  };
  return (
    <>
      <Button
        icon="pi pi-filter"
        label={T("word.filters")}
        className="component-editor-button icon-blue"
        onClick={() => setFilterVisible(true)}
      />
      <Sidebar
        visible={filterVisible}
        position="left"
        style={{ width: "35rem" }}
        onHide={() => setFilterVisible(false)}
        blockScroll
      >
        <div className="flex flex-column justify-content-between h-full">
          <div className="flex flex-column gap-2">
            <h2>{T("word.filters")}</h2>
            {attributes &&
              filters.map((f, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={i} className="p-inputgroup p-inputtext-sm">
                  <Dropdown
                    value={f.reference}
                    options={attributes}
                    onChange={e => handleChange(e, i)}
                    name="reference"
                    optionLabel="displayName"
                    optionValue="referenceName"
                    placeholder={T("word.attribute")}
                  />
                  <Dropdown
                    value={f.validation}
                    options={setValidationOptions(attributes?.find(a => a?.referenceName === f.reference)?.type)}
                    onChange={e => handleChange(e, i)}
                    name="validation"
                    optionLabel="display"
                    optionValue="short"
                    placeholder={T("word.validation")}
                  />
                  <InputText value={f.value} onChange={e => handleChange(e, i)} name="value" placeholder={T("word.value")} />
                  <Button icon="pi pi-times" className="p-button-danger" onClick={() => removeFilter(i)} />
                </div>
              ))}
            {error && <span className="p-error">{error}</span>}
          </div>
          <div className="flex justify-content-between">
            <Button label={T("action.add-filter")} onClick={addNewFilter} className="w-8rem" />
            <Button
              label={T("action.apply")}
              icon="pi pi-check"
              className="w-8rem align-self-end p-button-success"
              onClick={onApply}
            />
          </div>
        </div>
      </Sidebar>
    </>
  );
};

export default ProductTemplateFilter;
